import React, { useState, useEffect } from 'react';
import Axios from "axios";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  Trash2, 
  Plus, 
  GripVertical, 
 
  Image, 
  FileText, 
  Briefcase, 
  CreditCard,
  ShieldAlert,
  GraduationCap
} from 'lucide-react';

// UI Components
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const backEndUrl = process.env.REACT_APP_SERVER_ADDRESS;

// --- MOCK DATA FOR QUICK ADD ---
// Using data structure inspired by mockup and existing app
const QUICK_ADD_CATEGORIES = [
    {
        title: "Identity & Status",
        items: [
            { id: 'passport', label: 'Passport', desc: 'Scan of biodata page', icon: CreditCard },
            { id: 'photo', label: 'Digital Photo', desc: '50mm x 70mm white background', icon: Image },
            { id: 'marriage', label: 'Marriage Certificate', desc: 'Proof of marital status', icon: FileText },
        ]
    },
    {
        title: "Financial & Work",
        items: [
            { id: 'funds', label: 'Proof of Funds', desc: 'Bank statements (last 4 months)', icon: CreditCard },
            { id: 'employment', label: 'Reference Letters', desc: 'Employment verification letters', icon: Briefcase },
            { id: 'education', label: 'ECA Report', desc: 'WES or ICAS evaluation', icon: GraduationCap },
        ]
    },
    {
        title: "Clearances",
        items: [
            { id: 'police', label: 'Police Certificate', desc: 'Criminal record check', icon: ShieldAlert },
            { id: 'medical', label: 'Medical Exam', desc: 'eMedical information sheet', icon: FileText },
        ]
    }
];

export default function DocTemplate() {
    const navigate = useNavigate();
    
    // State
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [userData, setUserData] = useState(null);
    const [templateName, setTemplateName] = useState("");
    const [documents, setDocuments] = useState([]);
    const [showSuccess, setShowSuccess] = useState(false);
    const [error, setError] = useState("");

    // Fetch User Data
    useEffect(() => {
        Axios({
           method: "GET",
           withCredentials: true,
           url: `${backEndUrl}/auth/consultantLog`,
        }).then((res) => {
           setUserData(res.data);
           setIsLoading(false);
        }).catch(err => {
           console.error(err);
           setIsLoading(false);
        });
     }, []);

    // Document Management
    const addDocument = (template = null) => {
        const newDoc = {
            id: Date.now(),
            name: template ? template.label : "",
            description: template ? template.desc : "",
            isMandatory: true,
            file: null,
            fileName: "",
            icon: template ? template.icon : FileText
        };
        setDocuments([...documents, newDoc]);
    };

    const removeDocument = (id) => {
        setDocuments(documents.filter(doc => doc.id !== id));
    };

    const updateDocument = (id, field, value) => {
        setDocuments(documents.map(doc => 
            doc.id === id ? { ...doc, [field]: value } : doc
        ));
    };

    // Submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!templateName.trim()) {
            setError("Please enter a template name.");
            return;
        }

        if (documents.length === 0) {
            setError("Please add at least one document.");
            return;
        }

        const invalidDocs = documents.some(doc => !doc.name.trim());
        if (invalidDocs) {
            setError("All documents must have a name.");
            return;
        }

        setIsSubmitting(true);
        setError("");

        const formData = new FormData();
        formData.append('name', templateName);
        // Note: 'description' for the template itself might not be supported by backend yet based on previous file search, 
        // so we focus on mandatory fields. If backend supports it later, add: formData.append('description', templateDescription);
        formData.append('docNumber', documents.length);

        documents.forEach((doc, index) => {
            formData.append(`documents[${index}][name]`, doc.name);
            formData.append(`documents[${index}][description]`, doc.description);
            formData.append(`documents[${index}][isMandatory]`, doc.isMandatory.toString());
            
            if (doc.file) {
                formData.append(`documents[${index}][doc_upload]`, '[object File]');
            } else {
                formData.append(`documents[${index}][doc_upload]`, 'none');
            }
        });

        documents.forEach(doc => {
            if (doc.file) {
                formData.append('doc_upload', doc.file);
            }
        });

        try {
            await Axios({
                method: "PUT",
                url: `${backEndUrl}/update/newTemp/` + userData.id,
                data: formData,
                withCredentials: true,
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            setShowSuccess(true);
            setTimeout(() => {
                navigate('/dashboard/templates');
            }, 1500);
        } catch (err) {
            console.error(err);
            setError("Failed to create template. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="h-screen flex items-center justify-center bg-slate-50">
                <CircularProgress />
            </div>
        );
    }

    return (
        <div className="h-screen flex flex-col bg-slate-50 font-sans">
            
            {/* TOP BAR */}
            <header className="bg-white border-b border-slate-200 px-8 py-4 flex justify-between items-center sticky top-0 z-20 shadow-sm">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => navigate(-1)}
                        className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-500 hover:bg-slate-100 transition"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div className="h-6 w-px bg-slate-200"></div>
                    <h1 className="text-lg font-bold text-slate-800">New Checklist Template</h1>
                </div>
                <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-400 mr-2">
                        {documents.length > 0 || templateName ? "Unsaved changes" : "No changes"}
                    </span>
                    <button 
                        onClick={() => navigate('/dashboard/templates')}
                        className="text-slate-500 hover:text-slate-800 font-medium px-4 py-2 text-sm transition-colors"
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="bg-sky-600 hover:bg-sky-700 text-white px-6 py-2 rounded-lg font-bold shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {isSubmitting && <CircularProgress size={16} color="inherit" />}
                        Save Template
                    </button>
                </div>
            </header>

            {/* MAIN BUILDER AREA */}
            <main className="flex-1 flex overflow-hidden">
                
                {/* LEFT: THE CANVAS (The List) */}
                <div className="flex-1 overflow-y-auto p-8 md:p-12 scroll-smooth">
                    <div className="max-w-3xl mx-auto space-y-8">
                        
                        {/* 1. Template Settings */}
                        <div className="bg-white p-6 rounded-xl shadow-sm">
                            <div className="grid grid-cols-1 gap-6">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Template Name</label>
                                    <input 
                                        type="text" 
                                        value={templateName}
                                        onChange={(e) => setTemplateName(e.target.value)}
                                        className="w-full text-xl font-bold text-slate-800 border-b-2 border-slate-100 focus:border-sky-500 outline-none pb-2 placeholder-slate-300 transition-colors bg-transparent" 
                                        placeholder="e.g. Spousal Sponsorship (In-Canada)"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* 2. The Document Stack */}
                        <div>
                            <div className="flex justify-between items-end mb-4">
                                <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Documents in this Checklist</h3>
                                <span className="text-xs text-slate-400">{documents.length} Items</span>
                            </div>

                            <div className="space-y-3" id="doc-list">
                                {documents.map((doc, index) => {
                                    const Icon = doc.icon || FileText;
                                    return (
                                        <div key={doc.id} className="group flex items-center justify-between p-4 bg-white rounded-xl hover:shadow-md transition-all">
                                            <div className="flex items-center gap-4 flex-1">
                                                <div className="text-slate-300 group-hover:text-slate-400 cursor-grab">
                                                    <GripVertical className="w-5 h-5" />
                                                </div>
                                                <div className="w-10 h-10 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center shrink-0">
                                                    <Icon className="w-5 h-5" />
                                                </div>
                                                <div className="flex-1 min-w-0 pr-4">
                                                    <input 
                                                        type="text"
                                                        value={doc.name}
                                                        onChange={(e) => updateDocument(doc.id, 'name', e.target.value)}
                                                        className="font-bold text-slate-800 text-sm w-full border-none p-0 focus:ring-0 bg-transparent placeholder:text-slate-400"
                                                        placeholder="Document Name"
                                                    />
                                                    <input 
                                                        type="text"
                                                        value={doc.description}
                                                        onChange={(e) => updateDocument(doc.id, 'description', e.target.value)}
                                                        className="text-xs text-slate-400 w-full border-none p-0 focus:ring-0 bg-transparent placeholder:text-slate-300 mt-0.5"
                                                        placeholder="Description / Requirements"
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-6 shrink-0">
                                                {/* Mandatory Toggle */}
                                                <div 
                                                    className="flex items-center gap-2 cursor-pointer select-none"
                                                    onClick={() => updateDocument(doc.id, 'isMandatory', !doc.isMandatory)}
                                                >
                                                    <span className={`text-[10px] font-bold uppercase tracking-wider transition-colors w-16 text-right ${doc.isMandatory ? 'text-slate-500' : 'text-slate-400'}`}>
                                                        {doc.isMandatory ? 'Mandatory' : 'Optional'}
                                                    </span>
                                                    <div className={`relative w-9 h-5 rounded-full transition-colors duration-200 ease-in-out ${doc.isMandatory ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                                                        <div className={`absolute top-1 left-1 bg-white w-3 h-3 rounded-full transition-transform duration-200 ease-in-out shadow-sm ${doc.isMandatory ? 'translate-x-4' : 'translate-x-0'}`}></div>
                                                    </div>
                                                </div>
                                                
                                                {/* Delete */}
                                                <button 
                                                    onClick={() => removeDocument(doc.id)}
                                                    className="text-slate-300 hover:text-red-500 transition-colors p-1"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Add New Placeholder */}
                                <button 
                                    onClick={() => addDocument()}
                                    className="w-full py-4 border-2 border-dashed border-slate-300 rounded-xl text-slate-500 font-medium hover:border-sky-400 hover:text-sky-600 hover:bg-sky-50 transition-all flex items-center justify-center gap-2 group"
                                >
                                    <Plus className="w-4 h-4 group-hover:scale-110 transition-transform" /> 
                                    Add Custom Document
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* RIGHT: THE TOOLBOX (Smart Suggestions) */}
                <div className="w-80 bg-white border-l border-slate-200 p-6 hidden lg:block overflow-y-auto">
                    <h3 className="text-sm font-bold text-slate-800 mb-4">Quick Add Library</h3>
                    <p className="text-xs text-slate-500 mb-6">Click to instantly add standard IRCC requirements.</p>

                    {QUICK_ADD_CATEGORIES.map((category, idx) => (
                        <div key={idx} className="mb-6">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">{category.title}</p>
                            <div className="space-y-2">
                                {category.items.map((item) => {
                                    const Icon = item.icon;
                                    return (
                                        <button 
                                            key={item.id}
                                            onClick={() => addDocument(item)}
                                            className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-all text-left group"
                                        >
                                            <div className="w-8 h-8 rounded bg-slate-100 text-slate-500 flex items-center justify-center group-hover:bg-white group-hover:text-sky-600 transition-colors">
                                                <Icon className="w-4 h-4" />
                                            </div>
                                            <span className="text-sm text-slate-600 font-medium group-hover:text-sky-700 transition-colors">{item.label}</span>
                                            <Plus className="w-3 h-3 text-slate-300 ml-auto group-hover:text-sky-500 opacity-0 group-hover:opacity-100 transition-all" />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>

            </main>

            {/* Error Snackbar */}
            <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError("")}>
                <Alert onClose={() => setError("")} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>

            {/* Success Snackbar */}
            <Snackbar open={showSuccess} autoHideDuration={2000} onClose={() => setShowSuccess(false)}>
                <Alert severity="success" sx={{ width: '100%' }}>
                    Template saved successfully!
                </Alert>
            </Snackbar>
        </div>
    );
}
